import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { apiClient } from '@/services/api';
import { analyticsService } from '@/services/analytics';
import { identityTraits } from '@/services/identity';
import { paymentService } from '@/services/paymentService';
import { prefetchGuideAfterPurchase, watchForLatePro } from '@/services/proStatus';
import type { PaymentResult, PurchaseOptions } from '@/services/paymentService';
import { useAppStore } from '@/store/appStore';
import { clearQueryCache } from '@/utils/userStorage';
import { ApiError } from '@/services/api/core';
import { restartSignIn } from '@/services/restartSignIn';
import { setupApi } from '@/services/api/setup';
import type { AppType, ReminderFrequency } from '@/types';
import type { SetupWizardState } from '@/features/Setup/types';

export const useVocabData = (appType: AppType) => {
    return useQuery({
        queryKey: ['vocab', appType],
        queryFn: async () => {
            const response = await apiClient.getVocabData(appType);
            if (!response.success || !response.data) {
                throw new Error(response.error || 'Failed to fetch vocab data');
            }
            return response.data;
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
        refetchOnWindowFocus: true,
        refetchOnMount: true,
    });
};

export const useHallouminatiDashboard = () => {
    // Key by locale: exam titles (and other copy) are localized server-side from
    // the user's stored locale, so a payload cached for one locale must not be
    // served after the user switches. Without this, the persisted 5-min-fresh
    // dashboard survives the post-language-change reload and the Exams list keeps
    // showing the old locale's month names. invalidateQueries(['hallouminati-dashboard'])
    // still matches every locale variant (prefix match).
    const locale = useAppStore((s) => s.user?.locale) || 'en';
    return useQuery({
        queryKey: ['hallouminati-dashboard', locale],
        queryFn: async () => {
            console.log('[useHallouminatiDashboard] Fetching dashboard data...');
            const response = await apiClient.getHallouminatiDashboard();
            if (!response.success || !response.data) {
                console.error('[useHallouminatiDashboard] Failed to fetch:', response.error);
                throw new Error(response.error || 'Failed to fetch dashboard data');
            }
            console.log('[useHallouminatiDashboard] Data fetched successfully');
            return response.data;
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
        refetchOnWindowFocus: true,
        refetchOnMount: true,
    });
};

export const useProfileData = (appType: AppType) => {
    const query = useQuery({
        queryKey: ['profile', appType],
        queryFn: async () => {
            const response = await apiClient.getProfileData(appType);
            if (!response.success || !response.data) {
                // The ApiError itself, so the page can tell a rejected session (401).
                const apiError = (response as { _apiError?: unknown })._apiError;
                if (apiError instanceof ApiError) throw apiError;
                throw new Error(response.error || 'Failed to fetch profile data');
            }
            return response.data;
        },
        staleTime: 5 * 60 * 1000,
    });

    // Re-identify from the profile payload, which is the freshest copy of the
    // user we ever hold — the store's copy is set at launch and only patched
    // in place after that, so the exam goals recorded by the setup flow reach
    // analytics here, minutes before the next launch would carry them.
    // useAnalyticsIdentity covers the launch itself; a repeat with nothing new
    // is dropped inside the service.
    useEffect(() => {
        if (query.isSuccess && query.data?.user) {
            const user = query.data.user;
            analyticsService.identify(user.id, identityTraits(user));
        }
    }, [query.isSuccess, query.data]);

    return query;
};

/**
 * The setup wizard's own state.
 *
 * Deliberately not part of the profile payload: the wizard runs on the first
 * launch, and the `setup` key it used to read was gated behind eligibility
 * rules a brand-new account could not pass, so the flow opened with no
 * sittings and no questions.
 *
 * `staleTime: 0` because the flow writes to exactly this state as the user
 * answers, and the wizard refetches nothing mid-flow — it advances on the tap
 * and queues the writes. The only fetch that matters is the one on mount.
 *
 * `select`: the query cache is persisted to disk with no buster, so a device
 * can restore a `['setup']` payload from before `intents` joined it
 * (2026-09-13). Every reader dereferences `intents` unguarded, and Profile ›
 * Your exams renders the restored copy before the mount refetch lands — it
 * crashed on exactly that (HALLOUMINATI_FRONT-19S). A payload of the old shape
 * reads as not loaded yet, which every reader already waits out.
 */
const currentSetupShape = (state: SetupWizardState): SetupWizardState | undefined =>
    state.intents ? state : undefined;

export const useSetupState = () => {
    return useQuery({
        queryKey: ['setup'],
        queryFn: async () => {
            const response = await setupApi.get();
            if (!response.success || !response.data) {
                throw new Error(response.error || 'Failed to load setup');
            }
            return response.data;
        },
        select: currentSetupShape,
        staleTime: 0,
        refetchOnWindowFocus: false,
    });
};

export const useUpdateSettings = (appType: AppType) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (settings: { locale?: string; quiz_language?: string; reminder_frequency?: ReminderFrequency }) => {
            const response = await apiClient.updateSettings(appType, settings);
            if (!response.success) {
                throw new Error(response.error || 'Failed to update settings');
            }
            return response;
        },
        onSuccess: () => {
            // Invalidate both profile and vocab queries to refetch
            queryClient.invalidateQueries({ queryKey: ['profile', appType] });
            queryClient.invalidateQueries({ queryKey: ['vocab', appType] });
            queryClient.invalidateQueries({ queryKey: ['words', appType] });
            if (appType === 'hallouminati') {
                queryClient.invalidateQueries({ queryKey: ['hallouminati-dashboard'] });
                queryClient.invalidateQueries({ queryKey: ['guide', 'chapters'] });
            }
        },
    });
};

/**
 * `mutate(returnTo)`: on the web, where to land after logout instead of `/` —
 * the gift page's «Not you?» passes its own path, so the auth gate sends the
 * visitor to login and back to the same gift (utils/loginReturn.ts).
 */
export const useLogout = () => {
    const queryClient = useQueryClient();

    return useMutation<void, Error, string | void>({
        mutationFn: async () => {
            await apiClient.logout();
        },
        onSuccess: (_data, returnTo) => restartSignIn(queryClient, returnTo || undefined),
    });
};

// Helper to invalidate dashboard after practice session
export const useInvalidateDashboard = () => {
    const queryClient = useQueryClient();

    return (appType: AppType) => {
        queryClient.invalidateQueries({ queryKey: ['vocab', appType] });
        queryClient.invalidateQueries({ queryKey: ['words', appType] });
        if (appType === 'hallouminati') {
            queryClient.invalidateQueries({ queryKey: ['hallouminati-dashboard'] });
            // A quiz moves «Ошибки», the stages and the counts on the topic page.
            queryClient.invalidateQueries({ queryKey: ['hallouminati-list'] });
            queryClient.invalidateQueries({ queryKey: ['topic-questions'] });
            queryClient.invalidateQueries({ queryKey: ['topicMastery'] });
        }
    };
};

export const useInitiatePayment = () => {
    const queryClient = useQueryClient();

    return useMutation<PaymentResult, Error, PurchaseOptions | void>({
        mutationFn: async (options) => {
            return await paymentService.purchase(options || undefined);
        },
        onSuccess: (result) => {
            if (result.success && !result.checkoutUrl) {
                // Purchase succeeded -- drop all cached data so pro state, guide, etc. refresh
                queryClient.clear();
                // Also update zustand store immediately so isPro checks work without refetch
                const currentUser = useAppStore.getState().user;
                if (currentUser) {
                    useAppStore.getState().setUser({ ...currentUser, is_pro: true });
                }
                // Pre-download the now-unlocked premium chapters for offline use (best-effort).
                // isPro=true: the user just upgraded, so fetch the full articles.
                void prefetchGuideAfterPurchase(queryClient, currentUser?.locale || 'en');
            } else if (!result.success && Capacitor.isNativePlatform() && paymentService.getPaymentMethod() === 'stripe') {
                // An in-app Stripe checkout that ended unconfirmed may still have
                // been paid — keep checking so it unlocks without a restart.
                watchForLatePro(queryClient);
            }
        },
    });
};

// --- GreekBrain subscription (recurring; separate from the one-time Hallouminati guide) ---

export const useSubscribeGreekBrain = () => {
    const queryClient = useQueryClient();

    return useMutation<PaymentResult, Error>({
        mutationFn: async () => paymentService.subscribeGreekBrain(),
        onSuccess: (result) => {
            // A native (StoreKit / Play) purchase completes in place — no
            // checkout redirect — so drop cached data and flip the flag now
            // rather than waiting for the next profile fetch.
            if (result.success && !result.checkoutUrl) {
                queryClient.clear();
                const currentUser = useAppStore.getState().user;
                if (currentUser) {
                    useAppStore.getState().setUser({ ...currentUser, is_greekbrain_subscribed: true });
                }
            }
        },
    });
};

export const useCancelGreekBrain = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async () => paymentService.cancelGreekBrain(),
        onSuccess: (result) => {
            if (!result.success) return;
            const currentUser = useAppStore.getState().user;
            if (currentUser) {
                useAppStore.getState().setUser({
                    ...currentUser,
                    greekbrain_subscription_status: 'on_grace_period',
                    greekbrain_cancels_at: result.cancels_at ?? currentUser.greekbrain_cancels_at ?? null,
                });
            }
            queryClient.invalidateQueries({ queryKey: ['profile'] });
        },
    });
};

export const useResumeGreekBrain = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async () => paymentService.resumeGreekBrain(),
        onSuccess: (result) => {
            if (!result.success) return;
            const currentUser = useAppStore.getState().user;
            if (currentUser) {
                useAppStore.getState().setUser({
                    ...currentUser,
                    greekbrain_subscription_status: 'active',
                    greekbrain_cancels_at: null,
                });
            }
            queryClient.invalidateQueries({ queryKey: ['profile'] });
        },
    });
};

export const useRestorePurchases = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async () => {
            return await paymentService.restorePurchases();
        },
        onSuccess: (result) => {
            if (result.restored) {
                queryClient.clear();
                const currentUser = useAppStore.getState().user;
                if (currentUser) {
                    useAppStore.getState().setUser({ ...currentUser, is_pro: true });
                }
                // Pull down the restored-premium chapters for offline use (best-effort).
                void prefetchGuideAfterPurchase(queryClient, currentUser?.locale || 'en');
            }
        },
    });
};

export const usePaywallData = () => {
    return useQuery({
        queryKey: ['paywall-data'],
        queryFn: async () => {
            const response = await apiClient.getPaywallData();
            if (!response.success || !response.data) {
                throw new Error(response.error || 'Failed to fetch paywall data');
            }
            return response.data;
        },
        staleTime: 0, // Always fresh for countdown accuracy
        gcTime: 0, // Don't cache
        // Fail fast: one quick retry, then surface the in-modal retry button
        // instead of spinning through the default 2 retries (~90s on timeout).
        retry: 1,
    });
};

export const useDeleteAccount = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async () => {
            const response = await apiClient.deleteAccount();
            if (!response.success) {
                throw new Error(response.error || 'Failed to delete account');
            }
            return response;
        },
        onSuccess: () => restartSignIn(queryClient),
    });
};

export const useResetProgress = (appType: AppType) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async () => {
            const response = await apiClient.resetProgress(appType);
            if (!response.success) {
                throw new Error(response.error || 'Failed to reset progress');
            }
            return response;
        },
        onSuccess: async () => {
            // Clear all cached queries to force fresh data reload. The on-disk
            // snapshot goes too: the persister writes the cleared cache only
            // after its throttle, so a reload right after would rehydrate the
            // pre-reset data — still within staleTime, so nothing refetches.
            queryClient.clear();
            await clearQueryCache();
        },
    });
};